import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import clsx from "clsx"
import { useTranslation } from "react-i18next"

import { popDialog, resetDialogs } from "@/store/dialogs-state"
import { Button } from "./Button"
import { Icon } from "../icons/Icon"

export type DialogContentSize = "md" | "md-h-auto" | "lg" | "lg-h-auto" | "sm-h-auto" | "auto"
const sizeClassMap: Record<DialogContentSize, string> = {
	lg: "w-[60vw] h-[80vh] min-w-[640px] min-h-[480px] max-w-[1280px] max-h-[960px]",
	"lg-h-auto": "w-[60vw] max-w-[1280px]",
	md: "w-[800px] h-[540px]",
	"md-h-auto": "w-[800px]",
	"sm-h-auto": "w-[600px]",
	auto: "",
}

export interface DialogProps extends Omit<
	DialogPrimitive.DialogProps,
	"onOpenChange" | "onOpenAutoFocus"
> {
	disableClose?: boolean
	onOpenChange?: (this: void, open: boolean) => void
	onOpenAutoFocus?: (this: void, e: Event) => void
	trackingViewName: string
	contentProps?: React.ComponentProps<typeof DialogPrimitive.Content>
	closeConfirm?: () => Promise<boolean>
	onAllDialogsClosed?: () => void
	canGoBack?: boolean
	backConfirm?: () => Promise<boolean>
	contentSize?: DialogContentSize
	bottomButtons?: React.ReactNode
	headerContent?: React.ReactNode
}

const Dialog = ({
	children,
	onOpenChange,
	closeConfirm,
	disableClose,
	canGoBack,
	backConfirm,
	trackingViewName,
	onAllDialogsClosed,
	contentProps,
	contentSize,
	bottomButtons,
	headerContent,
	onOpenAutoFocus,
	...rootProps
}: DialogProps) => {
	const {
		ref: providedContentRef,
		onKeyDown: providedContentKeyDown,
		className: providedContentClassName,
		onOpenAutoFocus: nestedOnOpenAutoFocus,
		...restContentProps
	} = contentProps ?? {}

	const mergedOnOpenAutoFocus =
		nestedOnOpenAutoFocus ?? onOpenAutoFocus ?? ((e) => e.preventDefault())

	const contentRef = React.useRef<HTMLDivElement | null>(null)
	const setContentRef = React.useCallback(
		(node: HTMLDivElement | null) => {
			contentRef.current = node
			if (!providedContentRef) {
				return
			}
			if (typeof providedContentRef === "function") {
				providedContentRef(node)
				return
			}
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- merging forwarded refs requires mutable ref access
			;(providedContentRef as React.MutableRefObject<HTMLDivElement | null>).current = node
		},
		[providedContentRef],
	)

	const triggerPrimaryAction = React.useCallback(() => {
		const container = contentRef.current
		if (!container) {
			return false
		}
		const primaryButton = container.querySelector<HTMLElement>(
			'[data-primary-action="true"]:not([aria-disabled="true"])',
		)
		if (!primaryButton || primaryButton.dataset.disableEnterKey === "true") {
			return false
		}
		primaryButton.click()
		return true
	}, [])

	const handleContentKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		providedContentKeyDown?.(event)
		if (
			event.defaultPrevented ||
			event.key !== "Enter" ||
			event.shiftKey ||
			event.altKey ||
			event.metaKey ||
			event.ctrlKey ||
			event.repeat
		) {
			return
		}

		const target = event.target
		if (!(target instanceof HTMLElement)) {
			return
		}
		if (target.isContentEditable || target.tagName === "TEXTAREA") {
			return
		}
		if (target.closest('[data-disable-enter-key="true"]')) {
			return
		}
		if (triggerPrimaryAction()) {
			event.preventDefault()
			event.stopPropagation()
		}
	}

	const contentClassName = clsx(
		sizeClassMap[contentSize ?? "auto"],
		"flex flex-1 flex-col",
		providedContentClassName,
	)
	const handleOpenChange = async (open: boolean) => {
		if (!open && !disableClose && (!closeConfirm || (await closeConfirm()) === true)) {
			resetDialogs()

			onOpenChange?.(open)
			onAllDialogsClosed?.()
		}
	}

	const handleBackButton = async () => {
		if (canGoBack && (!backConfirm || (await backConfirm()) === true)) {
			popDialog()
		}
	}
	return (
		<DialogPrimitive.Root
			defaultOpen
			onOpenChange={(open) => void handleOpenChange(open)}
			{...rootProps}
		>
			<DialogContent
				ref={setContentRef}
				className={contentClassName}
				aria-label={trackingViewName}
				disableClose={disableClose}
				onOpenAutoFocus={mergedOnOpenAutoFocus}
				closeButtonClicked={() => void handleOpenChange(false)}
				canGoBack={canGoBack}
				headerContent={headerContent}
				backButtonClicked={() => void handleBackButton()}
				{...restContentProps}
				onKeyDown={handleContentKeyDown}
			>
				<DialogPrimitive.Title hidden className="sr-only">
					{trackingViewName ?? "Dialog"}
				</DialogPrimitive.Title>
				<DialogPrimitive.Description hidden className="sr-only">
					{trackingViewName ? `${trackingViewName} dialog` : "Application dialog"}
				</DialogPrimitive.Description>
				{children}
				{bottomButtons ? bottomButtons : undefined}
			</DialogContent>
		</DialogPrimitive.Root>
	)
}

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = (props: DialogPrimitive.DialogPortalProps) => (
	<DialogPrimitive.Portal {...props} />
)
DialogPortal.displayName = DialogPrimitive.Portal.displayName

const DialogOverlay = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Overlay>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Overlay
		ref={ref}
		className={clsx(
			"bg-background/80 fixed inset-0 z-50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
			className,
		)}
		{...props}
	/>
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
		disableClose?: boolean
		closeButtonClicked?: () => void
		canGoBack?: boolean
		headerContent?: React.ReactNode
		backButtonClicked?: () => void
	}
>(
	(
		{
			className,
			children,
			canGoBack,
			headerContent,
			backButtonClicked,
			disableClose,
			closeButtonClicked,
			...props
		},
		ref,
	) => {
		const { t } = useTranslation("components")
		return (
			<DialogPrimitive.Content
				ref={ref}
				className={clsx(
					"fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%] justify-start space-y-3 overflow-hidden bg-base-800 p-5 shadow-lg sm:rounded-lg",
					className,
				)}
				forceMount
				aria-describedby={className}
				{...props}
			>
				{(!disableClose || canGoBack || headerContent) && (
					<div className="z-50 flex w-full shrink-0 flex-row items-center justify-between gap-3 p-0">
						{canGoBack && (
							<Button
								trackingLabel="dialog_back"
								aria-label={t("dialog.ariaGoBack")}
								variant="ghost"
								size="custom"
								className="p-0 text-base-400 hover:text-base-200"
								onClick={() => {
									backButtonClicked?.()
								}}
							>
								<Icon name="arrow_back" size={20} />
							</Button>
						)}
						{!canGoBack && <div></div>}
						{headerContent}
						{!disableClose && (
							<Button
								trackingLabel="dialog_close"
								aria-label={t("dialog.ariaCloseDialog")}
								variant="ghost"
								size="custom"
								className="p-0 text-base-400 hover:text-base-200"
								onClick={() => {
									closeButtonClicked?.()
								}}
							>
								<Icon name="close" size={20} />
							</Button>
						)}
					</div>
				)}
				{children}
			</DialogPrimitive.Content>
		)
	},
)
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={clsx("flex flex-col space-y-1.5 text-center sm:text-left", className)}
		{...props}
	/>
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={clsx("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
		{...props}
	/>
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Title>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Title
		ref={ref}
		className={clsx("text-lg font-semibold leading-none tracking-tight", className)}
		{...props}
	/>
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Description>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Description
		ref={ref}
		className={clsx("text-muted-foreground text-sm", className)}
		{...props}
	/>
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
	Dialog,
	DialogTrigger,
	DialogContent,
	DialogHeader,
	DialogFooter,
	DialogTitle,
	DialogDescription,
}
